import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "./ui/drawer";
import { Button } from "./ui/button";
import { createCheckoutSession } from "../lib/api";
import { usePricing } from "../lib/usePricing";
import { useIsMobile } from "../lib/useIsMobile";

const FEATURES = [
  "Routine Checker — your whole routine at once",
  "Discussion Report — a PDF for your prescriber, sources linked",
  "What to use instead — safer products reviewed with your meds",
];

export default function PaywallModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const pricing = usePricing();

  async function handleUpgrade() {
    setLoading(true);
    try {
      const { url } = await createCheckoutSession("pro_monthly");
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Checkout failed";
      toast.error("Couldn't open checkout", { description: msg });
      setLoading(false);
    }
  }

  const body = (
    <div className="p-6 sm:p-10">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
        <Sparkles className="w-3.5 h-3.5 text-peach-500" />
        Discussion report · Pro
      </div>
      {isMobile ? (
        <DrawerTitle className="font-bold text-2xl sm:text-3xl mt-3 tracking-[-0.02em] text-ink text-left">
          Get Your{" "}
          <span className="font-editorial italic font-normal text-peach-500">
            Dermatologist Discussion Report
          </span>
        </DrawerTitle>
      ) : (
        <DialogTitle className="font-bold text-2xl sm:text-3xl mt-4 tracking-[-0.02em] text-ink">
          Get Your{" "}
          <span className="font-editorial italic font-normal text-peach-500">
            Dermatologist Discussion Report
          </span>
        </DialogTitle>
      )}
      {isMobile ? (
        <DrawerDescription className="text-ink-soft/85 mt-2 text-[15px] leading-relaxed text-left">
          Download a report of your check — formatted to bring to your next
          dermatology appointment, with every finding linked to its source.
        </DrawerDescription>
      ) : (
        <DialogDescription className="text-ink-soft/85 mt-3 text-[15px] leading-relaxed">
          Download a report of your check — formatted to bring to your next
          dermatology appointment, with every finding linked to its source.
        </DialogDescription>
      )}

      <div className="mt-6 sm:mt-7 rounded-3xl border border-surface-line bg-white p-5 sm:p-7 shadow-subtle">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-extrabold text-4xl sm:text-5xl text-ink tracking-[-0.025em]">
            {pricing.formatted}
          </span>
          <span className="text-ink-mute text-sm">/{pricing.interval}</span>
          <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-safe-bg text-safe-deep border border-safe-border">
            Cancel anytime
          </span>
        </div>
        <ul className="mt-5 sm:mt-6 space-y-3">
          {FEATURES.map((f) => (
            <li
              key={f}
              data-testid={`paywall-feature-${f.replace(/\s+/g, "-").toLowerCase()}`}
              className="flex items-start gap-3 text-[14px] text-ink-soft"
            >
              <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-safe-bg text-safe-deep border border-safe-border shrink-0">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>
        <Button
          data-testid="paywall-upgrade-btn"
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full h-14 rounded-2xl mt-6 sm:mt-7 bg-ink hover:bg-ink-soft text-cream-50 text-[15px] font-medium transition-all hover:scale-[1.01] active:scale-[0.99]"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Opening checkout…
            </>
          ) : (
            <>
              Start Pro — {pricing.formatted}/{pricing.interval}
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
        <p
          data-testid="paywall-subtext"
          className="text-[12.5px] text-ink-mute text-center mt-3"
        >
          Founding members get Pro free for 12 months.{" "}
          <Link to="/pro" className="underline hover:text-ink" onClick={() => onOpenChange(false)}>
            See everything in Pro
          </Link>
        </p>
      </div>

      <button
        data-testid="paywall-dismiss-btn"
        onClick={() => onOpenChange(false)}
        className="text-[13px] text-ink-mute hover:text-ink transition-colors w-full text-center mt-5 min-h-[44px]"
      >
        Maybe later
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          data-testid="paywall-modal"
          className="bg-cream-50 border-surface-line max-h-[94vh]"
        >
          <div className="overflow-y-auto">{body}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="paywall-modal"
        className="sm:max-w-lg p-0 overflow-hidden rounded-3xl border border-surface-line bg-cream-50 shadow-floating"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
}
